import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, isValid } from "date-fns";
import {
  getContractor,
  updateContractor,
} from "../services/contractorsService";
import s3Service from "../services/s3Service";
import S3ImageUpload from "../components/S3ImageUpload";
import styles from "../assets/contractors.module.css";

const ContractorEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contractor_id: "",
    email: "",
    email_alternative: "",
    logo: null,
    logoKey: "",
    logoName: "",
    logoType: "",
    logoSize: 0,
    date_review: "",
    address_1: "",
    address_2: "",
    address_3: "",
    town: "",
    postcode: "",
    phone_number: "",
    phone_number_alternative: "",
    fax_number: "",
    web: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  // Load contractor data
  useEffect(() => {
    loadContractor();
  }, [id]);

  const loadContractor = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getContractor(id);

      if (response.success) {
        const contractor = response.data;
        
        // Load logo from S3 if it exists
        let logoKey = "";
        if (contractor.logo) {
          try {
            // Logo is stored as S3 key (e.g., "uploads/images/contractors/filename.jpg")
            logoKey = contractor.logo;
          } catch (error) {
            console.error("Error processing logo:", error);
          }
        }
        
        setFormData({
          name: contractor.name || "",
          contractor_id: contractor.contractor_id || "",
          email: contractor.email || "",
          email_alternative: contractor.email_alternative || "",
          logo: null, // Logo will be handled separately for file upload
          logoKey: logoKey,
          logoName: "",
          logoType: "",
          logoSize: 0,
          date_review: contractor.date_review
            ? formatDateForInput(contractor.date_review)
            : "",
          address_1: contractor.address_1 || "",
          address_2: contractor.address_2 || "",
          address_3: contractor.address_3 || "",
          town: contractor.town || "",
          postcode: contractor.postcode || "",
          phone_number: contractor.phone_number || "",
          phone_number_alternative: contractor.phone_number_alternative || "",
          fax_number: contractor.fax_number || "",
          web: contractor.web || "",
          notes: contractor.notes || "",
        });
      } else {
        setError("Failed to load contractor");
        toast.error("Failed to load contractor");
      }
    } catch (error) {
      console.error("Error loading contractor:", error);
      setError("Error loading contractor");
      toast.error("Error loading contractor");
    } finally {
      setLoading(false);
    }
  };

  // Parse stored yyyy-MM-dd (or ISO) string into a Date for the picker
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  };

  // Format from backend datetime string to yyyy-MM-dd for internal state
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (!isValid(date)) return "";
    return format(date, "yyyy-MM-dd");
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleImageUpload = (imageKey, file) => {
    setFormData((prev) => ({
      ...prev,
      logoKey: imageKey || "",
      logoName: file ? file.name : "",
      logoType: file ? file.type : "",
      logoSize: file ? file.size : 0,
      logo: file || null,
    }));

    // Clear logo error when new image is uploaded
    if (errors.logo) {
      setErrors((prev) => ({
        ...prev,
        logo: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.contractor_id.trim()) {
      newErrors.contractor_id = "Contractor ID is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.email_alternative &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_alternative)
    ) {
      newErrors.email_alternative = "Please enter a valid email address";
    }

    // Max length validation (matching Laravel UpdateContractorRequest)
    const maxLengths = {
      name: 255,
      contractor_id: 255,
      email: 100,
      email_alternative: 100,
      address_1: 255,
      address_2: 255,
      address_3: 255,
      town: 255,
      postcode: 255,
      phone_number: 255,
      phone_number_alternative: 255,
      fax_number: 255,
      web: 255,
    };

    for (const [field, maxLength] of Object.entries(maxLengths)) {
      if (formData[field] && formData[field].length > maxLength) {
        newErrors[field] = `The ${field.replace(/_/g, " ")} must not be greater than ${maxLength} characters.`;
      }
    }

    // Logo validation is handled by S3ImageUpload component

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      // Prepare data for submission (use JSON format with S3 key, not FormData)
      const submitData = {
        name: formData.name,
        contractor_id: formData.contractor_id,
        email: formData.email || "",
        email_alternative: formData.email_alternative || "",
        logoKey: formData.logoKey || "", // Use S3 key instead of file
        date_review: formData.date_review || null,
        address_1: formData.address_1 || "",
        address_2: formData.address_2 || "",
        address_3: formData.address_3 || "",
        town: formData.town || "",
        postcode: formData.postcode || "",
        phone_number: formData.phone_number || "",
        phone_number_alternative: formData.phone_number_alternative || "",
        fax_number: formData.fax_number || "",
        web: formData.web || "",
        notes: formData.notes || "",
      };

      const response = await updateContractor(id, submitData);

      if (response.success) {
        toast.success("Contractor updated successfully");
        navigate("/contractors");
      } else {
        toast.error(response.message || "Failed to update contractor");
      }
    } catch (error) {
      console.error("Error updating contractor:", error);
      toast.error("Error updating contractor");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.contractorsContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.contractorsContainer}>
        <div className={styles.errorContainer}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.contractorsContainer}
      style={{ padding: "0px 200px" }}
    >
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/dashboard" className={styles.breadcrumbItem}>
          Dashboard
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/contractors" className={styles.breadcrumbItem}>
          Contractors
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbItem}>{formData.name}</span>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Edit Contractor</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  {/* Contractor Name */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Name <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.name ? styles.hasError : ""
                        }`}
                        required
                      />
                      {errors.name && (
                        <span className={styles.errorMessage}>
                          {errors.name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contractor ID */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Contractor ID <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="contractor_id"
                        value={formData.contractor_id}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.contractor_id ? styles.hasError : ""
                        }`}
                        required
                      />
                      {errors.contractor_id && (
                        <span className={styles.errorMessage}>
                          {errors.contractor_id}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Email</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.email ? styles.hasError : ""
                        }`}
                      />
                      {errors.email && (
                        <span className={styles.errorMessage}>
                          {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Alternative Email */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Alternative Email
                    </label>
                    <div className={styles.inputGroup}>
                      <input
                        type="email"
                        name="email_alternative"
                        value={formData.email_alternative}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.email_alternative ? styles.hasError : ""
                        }`}
                      />
                      {errors.email_alternative && (
                        <span className={styles.errorMessage}>
                          {errors.email_alternative}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Logo */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Logo</label>
                    <div className={styles.inputGroup}>
                      <S3ImageUpload
                        onImageUpload={handleImageUpload}
                        currentImage={formData.logoKey}
                        accept="image/*"
                        maxSize={100 * 1024 * 1024} // 100MB
                        className={styles.formControl}
                        helpText="The logo must be a clear image of your company logo. Allowed formats: JPEG, PNG, GIF, WebP, BMP. Maximum file size: 100MB. If you don't want to change file - just leave the field empty."
                        showPreview={true}
                        exampleImage={null}
                        folder="uploads/images/contractors"
                      />
                      {errors.logo && (
                        <span className={styles.errorMessage}>
                          {errors.logo}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Review Date */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Review Date</label>
                    <div className={styles.inputGroup}>
                      <DatePicker
                        selected={parseDate(formData.date_review)}
                        onChange={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            date_review:
                              date && isValid(date)
                                ? format(date, "yyyy-MM-dd")
                                : "",
                          }))
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/yyyy"
                        className={styles.formControl}
                        autoComplete="off"
                        isClearable
                      />
                    </div>
                  </div>

                  {/* Address 1 */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Address 1</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="address_1"
                        value={formData.address_1}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Address 2 */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Address 2</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="address_2"
                        value={formData.address_2}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Address 3 */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Address 3</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="address_3"
                        value={formData.address_3}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Town */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Town</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="town"
                        value={formData.town}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Postcode */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Postcode</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Phone Number</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Alternative Phone Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Alternative Phone Number
                    </label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="phone_number_alternative"
                        value={formData.phone_number_alternative}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Fax Number */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Fax Number</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="fax_number"
                        value={formData.fax_number}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  {/* Web */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Web</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        name="web"
                        value={formData.web}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          errors.web ? styles.hasError : ""
                        }`}
                      />
                      {errors.web && (
                        <span className={styles.errorMessage}>
                          {errors.web}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Notes</label>
                    <div className={styles.inputGroup}>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        rows="4"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className={styles.formGroup}>
                    <div className={styles.buttonContainer}>
                      <button
                        type="submit"
                        className={styles.btnHead}
                        disabled={saving}
                      >
                        <i className={styles.glyphiconCheck}></i>
                        {saving ? "Saving..." : "Save Contractor"}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorEdit;
